import type { AdminRole } from './session';

/**
 * Which sections each role is shown (06 §1).
 *
 * ⚠️ **This is presentation, not authorisation.** The server owns access: every
 * admin controller carries `@AdminRoles(...)` and answers `403 INSUFFICIENT_ROLE`
 * to anything else, whatever this file says. Hiding a section keeps an admin
 * from walking into a wall — it does not keep anyone out, and no decision here
 * should ever be described as the control.
 *
 * The role sets below mirror the server's decorators rather than 06's prose
 * where the two differ. See `ANALYTICS_ROLES`.
 */

export const ALL_ROLES: readonly AdminRole[] = [
  'SUPER_ADMIN',
  'CONTENT_ADMIN',
  'SUPPORT',
  'ANALYST',
];

/**
 * ⚠️ 06 §2 reads "Analytics — `ANALYST` and above", which would include
 * `SUPPORT` and `CONTENT_ADMIN`. `AnalyticsAdminController` is decorated
 * `@AdminRoles('SUPER_ADMIN', 'ANALYST')`, so those two roles would see a
 * section that returns 403 on every request. The server wins.
 */
export const ANALYTICS_ROLES: readonly AdminRole[] = ['SUPER_ADMIN', 'ANALYST'];
export const CONTENT_ROLES: readonly AdminRole[] = ['SUPER_ADMIN', 'CONTENT_ADMIN'];
export const SUPPORT_ROLES: readonly AdminRole[] = ['SUPER_ADMIN', 'SUPPORT'];
export const SUPER_ONLY: readonly AdminRole[] = ['SUPER_ADMIN'];

export interface NavItem {
  path: string;
  label: string;
  roles: readonly AdminRole[];
}

export interface NavSection {
  label: string;
  items: NavItem[];
}

const SECTIONS: NavSection[] = [
  {
    label: 'Overview',
    items: [{ path: '/', label: 'Dashboard', roles: ALL_ROLES }],
  },
  {
    label: 'Master data',
    items: [
      { path: '/interests', label: 'Interests', roles: CONTENT_ROLES },
      { path: '/event-types', label: 'Event types', roles: CONTENT_ROLES },
      { path: '/option-sets', label: 'Option sets', roles: CONTENT_ROLES },
      { path: '/categories', label: 'Wishlist categories', roles: CONTENT_ROLES },
      { path: '/marketplaces', label: 'Marketplaces', roles: CONTENT_ROLES },
    ],
  },
  {
    label: 'Content',
    items: [
      { path: '/wizards', label: 'Wizard builder', roles: CONTENT_ROLES },
      { path: '/catalogue', label: 'Curated catalogue', roles: CONTENT_ROLES },
      { path: '/calendar-events', label: 'Calendar events', roles: CONTENT_ROLES },
      { path: '/settings/support', label: 'Help and support', roles: CONTENT_ROLES },
      { path: '/legal/privacy-policy', label: 'Privacy policy', roles: CONTENT_ROLES },
      { path: '/legal/terms-of-service', label: 'Terms of service', roles: CONTENT_ROLES },
    ],
  },
  {
    label: 'People',
    items: [
      { path: '/users', label: 'Users', roles: SUPPORT_ROLES },
      { path: '/moderation', label: 'Moderation', roles: SUPPORT_ROLES },
      { path: '/search-anomalies', label: 'Search anomalies', roles: SUPPORT_ROLES },
    ],
  },
  {
    label: 'Analytics',
    items: [
      { path: '/analytics/clicks', label: 'Clicks', roles: ANALYTICS_ROLES },
      { path: '/analytics/top-products', label: 'Top products', roles: ANALYTICS_ROLES },
      { path: '/analytics/vendor-report', label: 'Vendor report', roles: ANALYTICS_ROLES },
    ],
  },
  {
    label: 'Administration',
    items: [
      { path: '/admins', label: 'Admin users', roles: SUPER_ONLY },
      { path: '/ops', label: 'Ops', roles: SUPER_ONLY },
    ],
  },
];

export function navFor(role: AdminRole): NavSection[] {
  return SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => item.roles.includes(role)),
  })).filter((section) => section.items.length > 0);
}

export function canAccess(role: AdminRole | undefined, path: string): boolean {
  if (!role) return false;
  const item = SECTIONS.flatMap((s) => s.items).find((i) => i.path === path);
  return item ? item.roles.includes(role) : false;
}

/**
 * Where a role lands after signing in. `ANALYST` has no PII access at all, so
 * their entry point is the analytics section rather than a dashboard that
 * would have to reason about which tiles it may draw.
 */
export function landingPathFor(role: AdminRole): string {
  return role === 'ANALYST' ? '/analytics/clicks' : '/';
}

export const ROLE_LABELS: Record<AdminRole, string> = {
  SUPER_ADMIN: 'Super admin',
  CONTENT_ADMIN: 'Content admin',
  SUPPORT: 'Support',
  ANALYST: 'Analyst',
};

export const ROLE_DESCRIPTIONS: Record<AdminRole, string> = {
  SUPER_ADMIN: 'Everything, including admin user management.',
  CONTENT_ADMIN: 'Master data, wizard, calendar events, curated catalogue.',
  SUPPORT: 'User lookup, suspend, resolve reports, view redacted child data.',
  ANALYST: 'Read-only analytics and exports. No access to personal data.',
};
